import { createTrackingConfig, getLabels } from '../../components/generic/labels';
import { getDateFormat, tableConfig, uiConfig } from '../../config/appConfig';
import CustomReturnedProductInspectionSplitView from './CustomReturnedProductInspectionSplitView';
import ReturnedProductInspectionReferenceDocument from './components/ReturnedProductInspectionReferenceDocument';

const trackingLabels = getLabels('Returned Product Inspection');
const trackingBase = createTrackingConfig('Returned Product Inspection');

const YES_NO_NA_OPTIONS = [
  { value: 'YES', label: 'Yes' },
  { value: 'NO', label: 'No' },
  { value: 'NA', label: 'N/A' },
];

const DISPOSITION_OPTIONS = [
  { value: 'RELEASE', label: 'Release for use' },
  { value: 'DESTROY', label: 'Destroy' },
];

export const returnedProductInspectionConfig = {
  entityName: 'ReturnedProductInspection',
  entityTypeId: 21,

  api: {
    baseEndpoint: '/returned-product-inspection',
    endpoints: {
      list: '',
      create: '',
      update: (id) => `/${id}`,
      delete: (id) => `/${id}`,
      getById: (id) => `/${id}`,
      findByStatus: (statusId) => `/status/${statusId}`,
    },
    lookupEndpoints: {
      users: '/users',
      customers: '/customer',
      returnedProductInspectionStatuses: '/returned-product-inspection-status',
    },
    swrInvalidation: {
      mainList: '/returned-product-inspection',
      fileAttachments: (entityTypeId) =>
        `/file-attachment/entity/${entityTypeId}/*`,
    },
  },

  ui: {
    drawerWidth: '50%',
    defaultPageSize: tableConfig.defaultPageSize,
    pageSizeOptions: tableConfig.pageSizeOptions,
    defaultTableDensity: tableConfig.defaultDensity,
    requiredFieldStyle: uiConfig.requiredFieldStyle,
    splitView: true,
    searchInputMaxWidth: uiConfig.searchInputMaxWidth,
    showViewButton: true,
    showEditButton: true,
    showDeleteButton: true,
    showDuplicateButton: false,
  },

  customSplitViewPanel: {
    enabled: true,
    component: CustomReturnedProductInspectionSplitView,
  },

  features: {
    fileAttachments: true,
    masterEntityTypeId: 21,
    approvalRequests: false,
  },

  displayName: 'Returned Product Inspection',
  description:
    'Returned goods inspection report (ΕΝ1 – Γ.Δ. 44/7) — one product per record',
  icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4',

  fieldsToRemove: [
    'id',
    'status',
    'customer',
    'inspectedBy',
    'dispositionBy',
    'createdBy',
    'updatedBy',
    'createdById',
    'updatedById',
    'createdAt',
    'updatedAt',
    'fileAttachments',
    'fileAttachmentCount',
  ],
  fieldsToKeep: [],

  fields: [
    { name: 'id', label: 'ID', type: 'hidden', table: false, filterable: false },
    {
      name: 'statusId',
      label: 'Status',
      type: 'hidden',
      required: false,
      defaultValue: 2, // Open
      lookupEndpoint: 'returnedProductInspectionStatuses',
      options: [],
      table: false,
      filterable: false,
      validation: { integer: true },
    },
    {
      name: 'itemCode',
      label: 'Item Code',
      type: 'text',
      required: true,
      placeholder: 'Product code',
      table: { header: 'Item Code', width: '120px', sortable: true },
      filterable: true,
      validation: {
        required: true,
        maxLength: 100,
        message: 'Item code is required',
      },
    },
    {
      name: 'productName',
      label: 'Product Name',
      type: 'text',
      required: true,
      placeholder: 'Product name',
      table: { header: 'Product', width: '200px', sortable: true },
      filterable: true,
      validation: {
        required: true,
        maxLength: 200,
        message: 'Product name is required',
      },
    },
    {
      name: 'batchNumber',
      label: 'Batch Number',
      type: 'text',
      required: true,
      placeholder: 'Batch / lot number',
      table: { header: 'Batch', width: '130px', sortable: true },
      filterable: true,
      validation: {
        required: true,
        maxLength: 100,
        message: 'Batch number is required',
      },
    },
    {
      name: 'expiryDate',
      label: 'Expiry Date',
      type: 'date',
      required: false,
      table: {
        header: 'Expiry',
        width: tableConfig.dateColumnWidth,
        sortable: true,
        cellRenderer: 'date',
        dateFormat: getDateFormat('table'),
      },
      filterable: true,
      filterType: 'dateRange',
    },
    {
      name: 'quantity',
      label: 'Quantity',
      type: 'number',
      required: true,
      placeholder: 'Qty',
      table: { header: 'Qty', width: '80px', sortable: true },
      filterable: true,
      validation: {
        required: true,
        integer: true,
        message: 'Quantity is required',
      },
    },
    {
      name: 'customerId',
      label: 'Customer',
      type: 'select',
      required: true,
      lookupEndpoint: 'customers',
      options: [],
      table: {
        header: 'Customer',
        width: '160px',
        sortable: true,
        displayField: 'customer',
        cellRenderer: 'badge',
      },
      filterable: true,
      filterType: 'multiSelect',
      validation: {
        required: true,
        integer: true,
        message: 'Please select a customer',
      },
    },
    {
      name: 'shipmentDate',
      label: 'Shipment Date',
      type: 'date',
      required: false,
      table: {
        header: 'Shipped',
        width: tableConfig.dateColumnWidth,
        sortable: true,
        cellRenderer: 'date',
        dateFormat: getDateFormat('table'),
      },
      filterable: true,
      filterType: 'dateRange',
    },
    {
      name: 'returnDate',
      label: 'Return Date',
      type: 'date',
      required: false,
      table: {
        header: 'Returned',
        width: tableConfig.dateColumnWidth,
        sortable: true,
        cellRenderer: 'date',
        dateFormat: getDateFormat('table'),
      },
      filterable: true,
      filterType: 'dateRange',
    },
    {
      name: 'coldChainCertificate',
      label: 'Cold-chain certificate (2–8°C) from customer',
      type: 'select',
      required: false,
      options: YES_NO_NA_OPTIONS,
      table: {
        header: 'Cold chain cert.',
        width: '120px',
        sortable: true,
        cellRenderer: 'badge',
      },
      filterable: true,
      filterType: 'multiSelect',
    },
    {
      name: 'receiptTemperature',
      label: 'Temperature on receipt',
      type: 'text',
      required: false,
      placeholder: 'e.g. 5.2 °C',
      table: {
        header: 'Temp.',
        width: '100px',
        sortable: true,
      },
      filterable: true,
      validation: { maxLength: 50 },
    },
    {
      name: 'icePacksUsed',
      label: 'Ice packs used',
      type: 'switch',
      required: false,
      defaultValue: false,
      table: {
        header: 'Ice packs',
        width: '100px',
        sortable: true,
        cellRenderer: 'booleanBadge',
      },
      filterable: true,
      filterType: 'boolean',
    },
    {
      name: 'packagingExcellentAppearance',
      label: 'Excellent appearance',
      type: 'switch',
      required: false,
      defaultValue: false,
      table: {
        header: 'Appearance OK',
        width: '110px',
        sortable: true,
        cellRenderer: 'booleanBadge',
      },
      filterable: true,
      filterType: 'boolean',
    },
    {
      name: 'packagingComplete',
      label: 'Complete packaging',
      type: 'switch',
      required: false,
      defaultValue: false,
      table: {
        header: 'Complete pkg',
        width: '110px',
        sortable: true,
        cellRenderer: 'booleanBadge',
      },
      filterable: true,
      filterType: 'boolean',
    },
    {
      name: 'packagingSealIntact',
      label: 'Authenticity seal intact',
      type: 'switch',
      required: false,
      defaultValue: false,
      table: {
        header: 'Seal intact',
        width: '110px',
        sortable: true,
        cellRenderer: 'booleanBadge',
      },
      filterable: true,
      filterType: 'boolean',
    },
    {
      name: 'verification',
      label: 'Verification',
      type: 'select',
      required: false,
      options: YES_NO_NA_OPTIONS,
      table: {
        header: 'Verification',
        width: '110px',
        sortable: true,
        cellRenderer: 'badge',
      },
      filterable: true,
      filterType: 'multiSelect',
    },
    {
      name: 'comments',
      label: 'Comments',
      type: 'textarea',
      required: false,
      rows: 3,
      placeholder: 'Inspection comments',
      table: {
        header: 'Comments',
        width: '180px',
        sortable: true,
        cellRenderer: 'truncateWithTooltip',
      },
      filterable: true,
    },
    {
      name: 'inspectedById',
      label: 'Inspected By',
      type: 'select',
      required: false,
      lookupEndpoint: 'users',
      labelField: 'userDisplayName',
      options: [],
      table: {
        header: 'Inspected By',
        width: '150px',
        sortable: true,
        displayField: 'inspectedBy',
        cellRenderer: 'userDisplay',
      },
      filterable: true,
      filterType: 'multiSelect',
      validation: { integer: true },
    },
    {
      name: 'inspectedAt',
      label: 'Inspection Date',
      type: 'date',
      required: false,
      table: {
        header: 'Inspected',
        width: tableConfig.dateColumnWidth,
        sortable: true,
        cellRenderer: 'date',
        dateFormat: getDateFormat('table'),
      },
      filterable: true,
      filterType: 'dateRange',
    },
    {
      name: 'disposition',
      label: 'Disposition',
      type: 'select',
      required: false,
      options: DISPOSITION_OPTIONS,
      table: {
        header: 'Disposition',
        width: '130px',
        sortable: true,
        cellRenderer: 'badge',
      },
      filterable: true,
      filterType: 'multiSelect',
    },
    {
      name: 'dispositionById',
      label: 'Disposition By',
      type: 'select',
      required: false,
      lookupEndpoint: 'users',
      labelField: 'userDisplayName',
      options: [],
      table: {
        header: 'Disposition By',
        width: '150px',
        sortable: true,
        displayField: 'dispositionBy',
        cellRenderer: 'userDisplay',
      },
      filterable: true,
      filterType: 'multiSelect',
      validation: { integer: true },
    },
    {
      name: 'dispositionAt',
      label: 'Disposition Date',
      type: 'date',
      required: false,
      table: {
        header: 'Disposition Date',
        width: tableConfig.dateColumnWidth,
        sortable: true,
        cellRenderer: 'date',
        dateFormat: getDateFormat('table'),
      },
      filterable: true,
      filterType: 'dateRange',
    },
    {
      name: 'referenceDocument',
      label: 'Reference Document',
      type: 'hidden',
      required: false,
      table: {
        header: 'Document Reference',
        width: '180px',
        sortable: true,
        cellRenderer: 'truncateWithTooltip',
        maxLength: 40,
      },
      filterable: true,
      filterType: 'text',
    },
    {
      name: 'fileAttachments',
      label: 'File(s)',
      type: 'fileUpload',
      useFormFileUpload2: true,
      multiple: true,
      accept: '.pdf,.doc,.docx,.txt,.md,image/*',
      maxSize: 50 * 1024 * 1024,
      maxFiles: 10,
      placeholder: 'Drop files here or click to browse',
      table: {
        header: 'File(s)',
        width: '50px',
        sortable: false,
        displayField: 'fileAttachmentCount',
        cellRenderer: 'fileCount',
      },
      filterable: false,
      validation: { required: false },
    },
  ],

  get tableColumns() {
    return this.fields
      .filter((field) => field.table !== false && field.table)
      .map((field) => ({
        key: field.table.displayField || field.name,
        header: field.table.header || field.label,
        width: field.table.width || 'auto',
        sortable: field.table.sortable !== false,
        displayField: field.table.displayField || field.name,
        cellRenderer: field.table.cellRenderer || 'default',
        dateFormat: field.table.dateFormat,
        originalField: field.name,
      }));
  },

  exportFileName: 'qms_returned_product_inspections',

  fieldMappings: {
    status: 'status.name',
    customer: 'customer.name',
    inspectedBy: 'inspectedBy.userDisplayName',
    dispositionBy: 'dispositionBy.userDisplayName',
  },

  formSections: [
    {
      title: 'Product',
      icon: '📦',
      layout: 'grid',
      gridCols: 'md:grid-cols-12',
      fields: [
        'itemCode',
        'productName',
        'batchNumber',
        'expiryDate',
        'quantity',
      ],
      fieldSpans: {
        itemCode: 'md:col-span-4',
        productName: 'md:col-span-4',
        batchNumber: 'md:col-span-4',
        expiryDate: 'md:col-span-4',
        quantity: 'md:col-span-4',
      },
    },
    {
      title: 'Customer & Dates',
      icon: '🏢',
      layout: 'grid',
      gridCols: 'md:grid-cols-12',
      fields: ['customerId', 'shipmentDate', 'returnDate'],
      fieldSpans: {
        customerId: 'md:col-span-6',
        shipmentDate: 'md:col-span-3',
        returnDate: 'md:col-span-3',
      },
    },
    {
      title: 'Receipt Inspection',
      icon: '🔍',
      layout: 'grid',
      gridCols: 'md:grid-cols-12',
      fields: [
        'coldChainCertificate',
        'receiptTemperature',
        'icePacksUsed',
        'packagingExcellentAppearance',
        'packagingComplete',
        'packagingSealIntact',
        'verification',
        'comments',
        'inspectedById',
        'inspectedAt',
      ],
      fieldSpans: {
        coldChainCertificate: 'md:col-span-4',
        receiptTemperature: 'md:col-span-4',
        icePacksUsed: 'md:col-span-4',
        packagingExcellentAppearance: 'md:col-span-4',
        packagingComplete: 'md:col-span-4',
        packagingSealIntact: 'md:col-span-4',
        verification: 'md:col-span-4',
        comments: 'md:col-span-8',
        inspectedById: 'md:col-span-6',
        inspectedAt: 'md:col-span-6',
      },
    },
    {
      title: 'Disposition',
      icon: '✅',
      layout: 'grid',
      gridCols: 'md:grid-cols-12',
      fields: ['disposition', 'dispositionById', 'dispositionAt'],
      fieldSpans: {
        disposition: 'md:col-span-4',
        dispositionById: 'md:col-span-4',
        dispositionAt: 'md:col-span-4',
      },
    },
    {
      title: 'File Attachments',
      icon: '📎',
      layout: 'full',
      fields: ['fileAttachments'],
    },
    {
      title: 'Reference Document',
      icon: '📄',
      layout: 'custom',
      showSectionHeader: true,
      customComponent: ReturnedProductInspectionReferenceDocument,
      fields: [],
    },
  ],

  trackingSection: {
    ...trackingBase,
    fields: trackingBase.fields.map((f) =>
      f.id === 'created'
        ? {
            ...f,
            userLabel: 'Created user:',
            dateLabel: trackingLabels.createdDateLabel,
            userEmptyText: trackingLabels.notSpecifiedText,
            dateEmptyText: trackingLabels.notSpecifiedText,
          }
        : f.id === 'updated'
          ? {
              ...f,
              userLabel: 'Updated user:',
              dateLabel: trackingLabels.updatedDateLabel,
              userEmptyText: trackingLabels.notModifiedText,
              dateEmptyText: trackingLabels.neverUpdatedText,
            }
          : f,
    ),
  },

  messages: {
    loading: 'Loading returned product inspections...',
    noData:
      'No returned product inspections found. Create your first record to get started.',
    createSuccess: 'Returned product inspection created successfully!',
    updateSuccess: 'Returned product inspection updated successfully!',
    deleteSuccess: 'Returned product inspection deleted successfully!',
    createError: 'Failed to create returned product inspection',
    updateError: 'Failed to update returned product inspection',
    deleteError: 'Failed to delete returned product inspection',
    loadError: 'Failed to load returned product inspections',
    validationError: 'Validation failed',
  },

  transformData: (data) => {
    const transformed = { ...data };

    delete transformed.fileAttachments;
    delete transformed.fileAttachmentCount;
    delete transformed.createdById;
    delete transformed.updatedById;
    delete transformed.createdAt;
    delete transformed.updatedAt;
    delete transformed.status;
    delete transformed.customer;
    delete transformed.inspectedBy;
    delete transformed.dispositionBy;
    delete transformed.createdBy;
    delete transformed.updatedBy;

    if (transformed.referenceDocument !== undefined) {
      if (
        transformed.referenceDocument === '' ||
        transformed.referenceDocument === null
      ) {
        delete transformed.referenceDocument;
      } else {
        transformed.referenceDocument = String(
          transformed.referenceDocument,
        ).trim();
      }
    }

    const foreignKeyFields = [
      'statusId',
      'customerId',
      'inspectedById',
      'dispositionById',
    ];
    foreignKeyFields.forEach((field) => {
      if (transformed[field] !== undefined) {
        if (transformed[field] === '' || transformed[field] === null) {
          transformed[field] = null;
        } else {
          const numValue = parseInt(transformed[field], 10);
          if (!isNaN(numValue)) {
            transformed[field] = numValue;
          } else {
            delete transformed[field];
          }
        }
      }
    });

    if (transformed.quantity !== undefined) {
      if (transformed.quantity === '' || transformed.quantity === null) {
        // leave for validation
      } else {
        const qty = parseInt(transformed.quantity, 10);
        transformed.quantity = !isNaN(qty) ? qty : transformed.quantity;
      }
    }

    const dateFields = [
      'expiryDate',
      'shipmentDate',
      'returnDate',
      'inspectedAt',
      'dispositionAt',
    ];
    dateFields.forEach((field) => {
      if (transformed[field] !== undefined) {
        if (transformed[field] === '' || transformed[field] === null) {
          transformed[field] = null;
        } else {
          const dateValue = new Date(transformed[field]);
          transformed[field] = !isNaN(dateValue.getTime()) ? dateValue : null;
        }
      }
    });

    const booleanFields = [
      'icePacksUsed',
      'packagingExcellentAppearance',
      'packagingComplete',
      'packagingSealIntact',
    ];
    booleanFields.forEach((field) => {
      if (transformed[field] !== undefined) {
        transformed[field] = Boolean(transformed[field]);
      }
    });

    ['coldChainCertificate', 'verification', 'disposition'].forEach((field) => {
      if (transformed[field] === '' || transformed[field] === undefined) {
        transformed[field] = null;
      }
    });

    return transformed;
  },

  validationConfig: {
    enableRealTimeValidation: true,
    validateOnBlur: true,
    validateOnChange: false,
    showValidationSummary: true,
  },
};

export default returnedProductInspectionConfig;
